import { z } from 'zod';
/**
 * What a chamber expects alongside a certificate, and what actually turned up.
 *
 * Shared front↔back so the upload form, the integration API and the console all describe a
 * supporting document the same way. Three ideas live here:
 *
 *  1. **A chamber states what it normally needs, per certificate type.** Not a rule engine —
 *     a list the submitter is shown before they send.
 *  2. **A missing required document WARNS. It never blocks.** An exporter with a genuine
 *     reason must still be able to lodge, and a chamber can always ask for clarification.
 *     Same principle as §2.3: the check flags, it does not gate.
 *  3. **Whether a document is normally stamped is the chamber's knowledge, not the
 *     exporter's.** A commercial invoice for the Middle East is stamped as well as the
 *     certificate; for most destinations it is not. The chamber encodes that once and the
 *     form arrives with the box already ticked.
 */
/** The state of one expected category against one submission. */
export const requiredDocumentStatusSchema = z.enum([
    /** Required by the chamber, and present. */
    'SATISFIED',
    /** Required by the chamber, and absent. Produces a warning, never a refusal. */
    'MISSING',
    /** Listed by the chamber as optional. Never warned about, present or not. */
    'OPTIONAL',
    /** Sent, but not something this certificate type asks for. Kept, never complained about. */
    'EXTRA',
]);
/** One row of a chamber's expectations for a certificate type. */
export const requiredDocumentRuleSchema = z.object({
    id: z.string(),
    category: z.string().trim().min(1).max(60),
    categoryLabel: z.string(),
    required: z.boolean(),
    /** Pre-ticks "stamp this too" for this category on this certificate type. */
    stampByDefault: z.boolean(),
    /** Shown to the submitter when it is not obvious why the chamber wants it. */
    note: z.string().max(400).nullable(),
    sortOrder: z.number().int(),
});
/** What a chamber admin saves for one certificate type. */
export const saveRequiredDocumentsSchema = z.object({
    documentTypeId: z.string().min(1),
    rules: z
        .array(z.object({
        category: z.string().trim().min(1).max(60),
        required: z.boolean().default(true),
        stampByDefault: z.boolean().default(false),
        note: z.string().trim().max(400).optional().nullable(),
    }))
        .max(20)
        // The same category twice is a configuration mistake, not two rules — and it would make
        // "is this satisfied?" ambiguous.
        .refine((rules) => new Set(rules.map((r) => r.category)).size === rules.length, 'The same document category is listed twice'),
});
/**
 * What a submitter is shown before they send, and what a warning is built from afterwards.
 *
 * Deliberately carries the label as well as the code: the submitter reads "Commercial
 * invoice", and every surface that shows this would otherwise have to re-resolve master
 * data to say so.
 */
export const requiredDocumentCheckSchema = z.object({
    category: z.string(),
    categoryLabel: z.string(),
    status: requiredDocumentStatusSchema,
    stampByDefault: z.boolean(),
    note: z.string().nullable(),
    /** Filenames that satisfied this category, for showing what was matched. */
    filenames: z.array(z.string()).default([]),
});
/**
 * The result of comparing what arrived against what the chamber asks for.
 *
 * `missing` is the actionable part and is duplicated out of `checks` so a caller building a
 * warning message does not have to filter. `satisfied` being false never means the
 * submission failed — only that the chamber will probably ask.
 */
export const requiredDocumentOutcomeSchema = z.object({
    satisfied: z.boolean(),
    checks: z.array(requiredDocumentCheckSchema),
    missing: z.array(z.object({ category: z.string(), categoryLabel: z.string() })).default([]),
});
/**
 * An operator's correction to a document that has already arrived.
 *
 * Every field is optional because the three things an operator does here are independent:
 * fix a category an email guessed wrong, tick "stamp this too" for a document the exporter
 * did not know needed it, or add the description the filename failed to convey.
 */
export const updateSupportingDocumentSchema = z
    .object({
    category: z.string().trim().min(1).max(60).optional(),
    description: z.string().trim().max(500).optional().nullable(),
    requestSignatureAndStamp: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Change at least one field');
//# sourceMappingURL=supporting-documents.js.map